import { Navigate } from 'react-router-dom';

import { call } from '@spacefill/shared/src/utils/Backend';
import { NavigateWithParams } from '@spacefill/shared/src/utils/RouterUtils';

const LogisticManagementStockAdjustmentByWarehouse = async () =>
    import('../../pages/logistic-management/inventory/stock-situation/StockAdjustment').then((module) => ({
        Component: module.LogisticManagementStockAdjustmentByWarehouse,
    }));
const LogisticManagementAccurateStockSituation = async () =>
    import('../../pages/logistic-management/inventory/accurate-stock-situation/index').then((module) => ({
        Component: module.LogisticManagementAccurateStockSituation,
    }));

const LogisticManagementOrdersRedirectView = async () =>
    import('../../pages/logistic-management/orders/RedirectView').then((module) => ({
        Component: module.LogisticManagementOrdersRedirectView,
    }));

const LogisticManagementOrdersImport = async () =>
    import('../../pages/logistic-management/orders/new/ImportOrders').then((module) => ({
        Component: module.LogisticManagementOrdersImport,
    }));

const LogisticManagementWarehousesOrdersList = async () =>
    import('../../pages/logistic-management/orders/list/List').then((module) => ({
        Component: module.LogisticManagementWarehousesOrdersList,
    }));
const LogisticManagementUnconfirmedOrdersList = async () =>
    import('../../pages/logistic-management/unconfirmed-orders/List').then((module) => ({
        Component: module.LogisticManagementUnconfirmedOrdersList,
    }));
const LogisticManagementUnconfirmedOrdersView = async () =>
    import('../../pages/logistic-management/unconfirmed-orders/View').then((module) => ({
        Component: module.LogisticManagementUnconfirmedOrdersView,
    }));
const LogisticManagementWarehousesOrdersView = async () =>
    import('../../pages/logistic-management/orders/view/View').then((module) => ({
        Component: module.LogisticManagementWarehousesOrdersView,
    }));
const NewOrderInit = async () =>
    import('../../pages/logistic-management/orders/new/Init').then((module) => ({
        Component: module.NewOrderInit,
    }));
const LogisticManagementWarehousesNewOrdersEntry = async () =>
    import('../../pages/logistic-management/orders/new/Entry').then((module) => ({
        Component: module.LogisticManagementWarehousesNewOrdersEntry,
    }));
const LogisticManagementWarehousesNewOrdersExit = async () =>
    import('../../pages/logistic-management/orders/new/Exit').then((module) => ({
        Component: module.LogisticManagementWarehousesNewOrdersExit,
    }));
const LogisticManagementWarehousesOrdersAdjustments = async () =>
    import('../../pages/logistic-management/orders/Adjustments').then((module) => ({
        Component: module.LogisticManagementWarehousesOrdersAdjustments,
    }));
const LogisticManagementWarehousesEditOrdersRedirectEntryOrExit = async () =>
    import('../../pages/logistic-management/orders/edit/index').then((module) => ({
        Component: module.LogisticManagementWarehousesEditOrdersRedirectEntryOrExit,
    }));
const LogisticManagementWarehousesEditOrders = async () =>
    import('../../pages/logistic-management/orders/edit/Edit').then((module) => ({
        Component: module.LogisticManagementWarehousesEditOrders,
    }));
const LogisticManagementMasterItemsListCustomUnit = async () =>
    import('../../pages/logistic-management/inventory/items/List').then((module) => ({
        Component: module.LogisticManagementMasterItemsList,
    }));
const LogisticManagementMasterItemsNewCustomUnit = async () =>
    import('../../pages/logistic-management/inventory/items/New').then((module) => ({
        Component: module.LogisticManagementMasterItemsNew,
    }));
const LogisticManagementMasterItemsEditCustomUnit = async () =>
    import('../../pages/logistic-management/inventory/items/Edit').then((module) => ({
        Component: module.LogisticManagementMasterItemsEdit,
    }));
const LogisticManagementMasterItemsViewCustomUnit = async () =>
    import('../../pages/logistic-management/inventory/items/View').then((module) => ({
        Component: module.LogisticManagementMasterItemsView,
    }));
const LogisticManagementDocumentsView = async () =>
    import('../../pages/logistic-management/documents/View').then((module) => ({
        Component: module.LogisticManagementDocumentsView,
    }));

const IncidentsList = async () =>
    import('../../pages/logistic-management/incidents/list/List').then((module) => ({
        Component: module.IncidentsList,
    }));
const AppointmentOrderModal = async () =>
    import('../../pages/logistic-management/orders/view/AppointmentModal/AppointmentOrderModal').then((module) => ({
        Component: module.AppointmentOrderModal,
    }));
const newAppointmentFromOrderIdLoader = async ({ params: { orderId, appointmentId } }) => {
    const order = await call(`orders/${orderId}`);
    return {
        order,
        appointment: order.transport?.appointments?.find((appointment) => appointment.id === appointmentId),
    };
};

const getLogisticProviderLogisticManagementRoutes = (user) => {
    if (user.role !== 'PROVIDER' || (user?.customerWarehouseConfigurations ?? []).length === 0) {
        return [];
    }

    return [
        // Global routes

        // Incidents List
        {
            path: '/logistic-management/incidents/',
            lazy: IncidentsList,
        },
        {
            path: '/logistic-management/incidents/view/:viewId',
            lazy: IncidentsList,
        },

        //  Documents
        {
            path: '/documents/',
            lazy: LogisticManagementDocumentsView,
        },

        // Orders
        {
            path: '/logistic-management/orders/',
            element: <NavigateWithParams to='/logistic-management/exit-orders/' />,
        },
        {
            path: '/logistic-management/entry-orders/',
            lazy: LogisticManagementWarehousesOrdersList,
        },
        {
            path: '/logistic-management/entry-orders/view/:viewId',
            lazy: LogisticManagementWarehousesOrdersList,
        },
        {
            path: '/logistic-management/exit-orders/',
            lazy: LogisticManagementWarehousesOrdersList,
        },
        {
            path: '/logistic-management/exit-orders/view/:viewId',
            lazy: LogisticManagementWarehousesOrdersList,
        },
        {
            path: '/logistic-management/entry-orders/init',
            lazy: NewOrderInit,
            conditionFailedRedirect: '/',
        },
        {
            path: '/logistic-management/exit-orders/init',
            lazy: NewOrderInit,
            conditionFailedRedirect: '/',
        },
        {
            path: '/logistic-management/orders/:orderId/',
            lazy: LogisticManagementOrdersRedirectView,
        },
        {
            path: '/logistic-management/orders/import/',
            lazy: LogisticManagementOrdersImport,
        },

        // Master items with Custom Unit
        {
            path: '/logistic-management/inventory/items/new',
            lazy: LogisticManagementMasterItemsNewCustomUnit,
        },
        {
            path: '/logistic-management/inventory/items/:masterItemId/edit',
            lazy: LogisticManagementMasterItemsEditCustomUnit,
        },
        {
            path: '/logistic-management/inventory/items/:masterItemId/',
            lazy: LogisticManagementMasterItemsViewCustomUnit,
        },
        {
            path: '/logistic-management/inventory/items/',
            lazy: LogisticManagementMasterItemsListCustomUnit,
        },
        {
            path: '/logistic-management/inventory/items/archived/',
            lazy: LogisticManagementMasterItemsListCustomUnit,
        },
        {
            path: '/logistic-management/inventory/items/unarchived/',
            lazy: LogisticManagementMasterItemsListCustomUnit,
        },

        // Stock situation
        {
            path: '/logistic-management/stock-situation/',
            lazy: LogisticManagementAccurateStockSituation,
        },
        {
            path: '/logistic-management/stock-situation/view/:viewId',
            lazy: LogisticManagementAccurateStockSituation,
        },
        {
            path: '/logistic-management/inventory/stock-situation/references/*',
            element: <Navigate to='/logistic-management/stock-situation/' />,
        },

        // By warehouse

        // Orders
        {
            path: '/logistic-management/unconfirmed-orders/',
            lazy: LogisticManagementUnconfirmedOrdersList,
        },
        {
            path: '/logistic-management/unconfirmed-orders/:unconfirmedOrderId/',
            lazy: LogisticManagementUnconfirmedOrdersView,
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/orders/',
            element: (
                <NavigateWithParams
                    to={(params) => `/logistic-management/warehouses/${params.warehouseId}/exit-orders/`}
                />
            ),
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/entry-orders/',
            lazy: LogisticManagementWarehousesOrdersList,
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/entry-orders/view/:viewId',
            lazy: LogisticManagementWarehousesOrdersList,
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/exit-orders/',
            lazy: LogisticManagementWarehousesOrdersList,
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/exit-orders/view/:viewId',
            lazy: LogisticManagementWarehousesOrdersList,
        },

        {
            path: '/logistic-management/warehouses/:warehouseId/entry-orders/init',
            lazy: NewOrderInit,
            conditionFailedRedirect: '/',
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/entry-orders/new',
            lazy: LogisticManagementWarehousesNewOrdersEntry,
            conditionFailedRedirect: '/',
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/exit-orders/init',
            lazy: NewOrderInit,
            conditionFailedRedirect: '/',
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/exit-orders/new',
            lazy: LogisticManagementWarehousesNewOrdersExit,
            conditionFailedRedirect: '/',
        },

        {
            path: '/logistic-management/warehouses/:warehouseId/orders/new-entry',
            lazy: LogisticManagementWarehousesNewOrdersEntry,
            conditionFailedRedirect: '/',
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/orders/new-exit',
            lazy: LogisticManagementWarehousesNewOrdersExit,
            conditionFailedRedirect: '/',
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/orders/:orderId/',
            lazy: LogisticManagementOrdersRedirectView,
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/entry-orders/:orderId/',
            lazy: LogisticManagementWarehousesOrdersView,
            children: [
                {
                    path: 'appointment/:appointmentId',
                    lazy: AppointmentOrderModal,
                    loader: newAppointmentFromOrderIdLoader,
                },
            ],
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/exit-orders/:orderId/',
            lazy: LogisticManagementWarehousesOrdersView,
            children: [
                {
                    path: 'appointment/:appointmentId',
                    lazy: AppointmentOrderModal,
                    loader: newAppointmentFromOrderIdLoader,
                },
            ],
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/entry-orders/:orderId/order-adjustment',
            lazy: LogisticManagementWarehousesOrdersAdjustments,
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/exit-orders/:orderId/order-adjustment',
            lazy: LogisticManagementWarehousesOrdersAdjustments,
        },

        // Edit order
        {
            path: '/logistic-management/warehouses/:warehouseId/orders/:orderId/edit',
            lazy: LogisticManagementWarehousesEditOrdersRedirectEntryOrExit,
        },

        {
            path: '/logistic-management/warehouses/:warehouseId/exit-orders/:orderId/edit',
            lazy: LogisticManagementWarehousesEditOrders,
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/entry-orders/:orderId/edit',
            lazy: LogisticManagementWarehousesEditOrders,
        },

        // Stock situation
        {
            path: '/logistic-management/warehouses/:warehouseId/stock-situation/',
            lazy: LogisticManagementAccurateStockSituation,
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/stock-situation/view/:viewId',
            lazy: LogisticManagementAccurateStockSituation,
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/inventory/stock-situation/references/*',
            element: (
                <NavigateWithParams
                    to={(params) => `/logistic-management/warehouses/${params.warehouseId}/stock-situation/`}
                />
            ),
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/inventory/stock-situation/new-adjustment/',
            lazy: LogisticManagementStockAdjustmentByWarehouse,
        },

        // Default
        {
            path: '/logistic-management/*',
            element: <Navigate to='/' />,
        },
    ];
};

export { getLogisticProviderLogisticManagementRoutes };
